import React, { useEffect, useMemo, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import { useAuth } from "../../context/AuthContext.tsx";
import * as S from "./ownerPay.style.ts";
import { ownerPayConfigAtom } from "../../hook/OwnerPayConfigAtom.ts";
import { incomeEventsAtom } from "../../hook/IncomeEventsAtom.ts";
import { recurringIncomeAtom } from "../../hook/RecurringIncomeAtom.ts";
import { budgetAtom } from "../../hook/BudgetAtom.ts";
import { cashAccountsAtom } from "../../hook/CashAccountsAtom.ts";
import { useMoney } from "../../hook/useMoney.ts";
import { IncomeMode, OwnerPayConfig, OwnerPayMethod } from "../../types.ts";
import {
  getRecommendedDraw,
  getRemainingCash,
  getTrailingIncomeStats,
} from "../../functions/cashflow.ts";
import {
  INCOME_MODE_OPTIONS,
  modeLabels,
} from "../../functions/cashflowCopy.ts";
import { persistOwnerPayConfig } from "../../functions/cashflowSync.ts";
import { trackEvent } from "../../functions/mixpanel.ts";
import Button from "../../components/Button/Button.tsx";
import { Navigate } from "react-router-dom";
import { userAtom } from "../../hook/UserAtom.ts";

const METHOD_LABELS: Record<OwnerPayMethod, string> = {
  trailing_median: "Typical month (median) — balanced",
  trailing_average: "Trailing average, minus a safety margin",
  lean_month: "Lean month (conservative)",
};

const OwnerPay = () => {
  const auth = useAuth();
  const [config, setConfig] = useAtom(ownerPayConfigAtom);
  const events = useAtomValue(incomeEventsAtom);
  const currentUser = useAtomValue(userAtom);
  const recurring = useAtomValue(recurringIncomeAtom);
  const budget = useAtomValue(budgetAtom);
  const accounts = useAtomValue(cashAccountsAtom);
  const { money } = useMoney();

  const [draft, setDraft] = useState<OwnerPayConfig>(config);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const labels = modeLabels(draft);
  const regular = draft.mode === "regular";
  const [showTax, setShowTax] = useState<boolean>(
    config.mode !== "regular" || config.taxReservePercent > 0,
  );

  // Pick up the stored config once it resolves (localStorage getOnInit timing,
  // or an async server hydrate) — but never stomp on edits in progress.
  useEffect(() => {
    if (!dirty) {
      setDraft(config);
      setShowTax(config.mode !== "regular" || config.taxReservePercent > 0);
    }
  }, [config, dirty]);

  const recommended = useMemo(() => {
    if (draft.mode === "regular") {
      return getRemainingCash(budget, events, accounts, new Date(), recurring);
    }
    const stats = getTrailingIncomeStats(events, draft, new Date(), recurring);
    return getRecommendedDraw(stats, draft);
  }, [budget, events, accounts, recurring, draft]);

  const update = (patch: Partial<OwnerPayConfig>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setDirty(true);
    setSaved(false);
  };

  const num =
    (key: keyof OwnerPayConfig, min: number, max: number) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Math.max(min, Math.min(max, Number(e.target.value) || 0));
      update({ [key]: value } as Partial<OwnerPayConfig>);
    };

  const setMode = (mode: IncomeMode) => {
    if (mode === "regular") {
      update({ mode, taxReservePercent: 0, method: "trailing_average" });
      setShowTax(false);
    } else {
      update({ mode });
      setShowTax(true);
    }
  };

  const save = () => {
    const next = showTax ? draft : { ...draft, taxReservePercent: 0 };
    setConfig(next);
    setDraft(next);
    setDirty(false);
    persistOwnerPayConfig(auth, next);
    trackEvent("Owner Pay - saved", { mode: next.mode, method: next.method });
    setSaved(true);
  };

  if (!currentUser?.hasBudget) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <S.Wrapper>
      <S.Title>{labels.ownerPayPageTitle}</S.Title>
      <S.Intro>{labels.ownerPayPageIntro}</S.Intro>

      <S.Field>
        How does your income arrive?
        <select
          value={draft.mode}
          onChange={(e) => setMode(e.target.value as IncomeMode)}
        >
          {INCOME_MODE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.title}
            </option>
          ))}
        </select>
      </S.Field>

      <S.Recommended>
        <div className="label">
          {regular ? "Suggested monthly budget" : "Recommended monthly draw"}
        </div>
        <div className="value">{money(recommended)}</div>
        <div className="sub">
          Based on your last {draft.trailingMonths} months of income
          {draft.taxReservePercent > 0
            ? `, after holding back ${draft.taxReservePercent}% for taxes.`
            : "."}
        </div>
      </S.Recommended>

      <S.Field>
        {regular ? "How to size the suggestion" : "Basis for the draw"}
        <select
          value={draft.method}
          onChange={(e) => update({ method: e.target.value as OwnerPayMethod })}
        >
          {(Object.keys(METHOD_LABELS) as OwnerPayMethod[]).map((method) => (
            <option key={method} value={method}>
              {METHOD_LABELS[method]}
            </option>
          ))}
        </select>
      </S.Field>

      <S.Field>
        Trailing window (months)
        <input
          type="number"
          min={1}
          max={24}
          value={draft.trailingMonths}
          onChange={num("trailingMonths", 1, 24)}
        />
        <span className="hint">
          How far back to look when sizing{" "}
          {regular ? "your budget" : "your pay"}.
        </span>
      </S.Field>

      <S.DrawRow>
        <S.Field>
          {labels.currentAmountField} (USD)
          <input
            type="number"
            min={0}
            value={draft.monthlyDraw}
            onChange={num("monthlyDraw", 0, 10_000_000)}
          />
        </S.Field>
        <Button
          buttonSize="small"
          handleClick={() => update({ monthlyDraw: recommended })}
        >
          Use suggested
        </Button>
      </S.DrawRow>

      <S.Field>
        {regular ? "Emergency fund target" : "Buffer target"} (months of
        essential expenses)
        <input
          type="number"
          min={0}
          max={12}
          value={draft.bufferTargetMonths}
          onChange={num("bufferTargetMonths", 0, 12)}
        />
      </S.Field>

      {regular && (
        <S.Field>
          <span>
            <input
              type="checkbox"
              checked={showTax}
              onChange={(e) => setShowTax(e.target.checked)}
            />{" "}
            I set aside my own taxes
          </span>
        </S.Field>
      )}

      {showTax && (
        <S.Field>
          Tax reserve (% of every payment)
          <input
            type="number"
            min={0}
            max={60}
            value={draft.taxReservePercent}
            onChange={num("taxReservePercent", 0, 60)}
          />
        </S.Field>
      )}

      <S.Field>
        Projection horizon (months)
        <input
          type="number"
          min={1}
          max={24}
          value={draft.projectionHorizonMonths}
          onChange={num("projectionHorizonMonths", 1, 24)}
        />
      </S.Field>

      <div>
        <Button buttonSize="medium" handleClick={save}>
          Save
        </Button>
      </div>
      {saved && <S.Saved>Saved.</S.Saved>}
    </S.Wrapper>
  );
};

export default OwnerPay;
